import React, { useState } from 'react';
import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { COMPONENT_ICONS } from '../canvas/CanvasBuilder/constants';
import { blockLabel, outlineRows } from './blockPresentation';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';
import styles from './Workspace.module.scss';

const BlockOutline = ({ canvas, selectedId, onSelect, onAdd, onMove, disabled, simple = false }) => {
  const t = useLocalizedMessage();
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [dragTarget, setDragTarget] = useState(null);
  const allRows = outlineRows(canvas, t);
  const needle = query.trim().toLowerCase();
  const rows = allRows.filter(row => {
    if (simple && row.container) return false;
    if (needle) return `${blockLabel(row.node, t, canvas?.root)} ${row.location}`.toLowerCase().includes(needle);
    return simple || !row.parents.some(id => collapsed[id]);
  });
  const handleDrop = (event, row) => {
    event.preventDefault();
    setDragTarget(null);
    const id = event.dataTransfer.getData('existingNodeId');
    if (!disabled && id) onMove(id, { nodeId: row.node.id, position: row.node.id === canvas.root ? 'inside' : 'before' });
  };

  return <div className={styles.outline}>
    <div className={styles.outlineSearch}>
      <TextField
        variant="outlined" size="small" fullWidth value={query}
        placeholder={t('experience.design.findBlock')}
        inputProps={{ 'aria-label': t('experience.design.findBlock') }}
        onChange={event => setQuery(event.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />
    </div>
    <div className={styles.outlineScroll} role="list" aria-label={t(simple ? 'experience.design.blocks' : 'experience.design.structure')}>
      {rows.map(row => {
        const { node, container, depth } = row;
        const Icon = COMPONENT_ICONS[node.type] || ViewModuleIcon;
        const selected = node.id === selectedId;
        return <div
          key={node.id} role="listitem" data-outline-node={node.id}
          className={`${styles.outlineRow} ${selected ? styles.selectedRow : ''} ${dragTarget === node.id ? styles.dropBefore : ''}`}
          style={{ '--node-depth': simple || needle ? 0 : Math.min(depth, 6) }}
          onDragOver={event => { if (!disabled) { event.preventDefault(); setDragTarget(node.id); } }}
          onDragLeave={() => setDragTarget(null)} onDrop={event => handleDrop(event, row)}
        >
          {!simple && container && <IconButton
            size="small" className={styles.expandButton} aria-label={t(collapsed[node.id] ? 'experience.design.expandBlock' : 'experience.design.collapseBlock', { name: blockLabel(node, t, canvas.root) })}
            aria-expanded={!collapsed[node.id]}
            onClick={() => setCollapsed(previous => ({ ...previous, [node.id]: !previous[node.id] }))}
          >{collapsed[node.id] ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>}
          <button
            type="button" className={styles.selectBlock} aria-pressed={selected}
            onClick={() => onSelect(node)} draggable={!disabled && node.id !== canvas.root}
            onDragStart={event => { event.dataTransfer.setData('existingNodeId', node.id); event.dataTransfer.effectAllowed = 'move'; }}
          >
            <Icon fontSize="small" />
            <span className={styles.blockName}>
              <span>{blockLabel(node, t, canvas.root)}</span>
              {row.location && (simple || depth === 1) && <small>{row.location}</small>}
            </span>
            {!container && <DragIndicatorIcon className={styles.dragHint} fontSize="small" />}
          </button>
          {!simple && container && <IconButton size="small" disabled={disabled} className={styles.expandButton}
            aria-label={t('experience.design.addTo', { name: blockLabel(node, t, canvas.root) })} onClick={() => onAdd(node.id)}><AddIcon fontSize="small" /></IconButton>}
        </div>;
      })}
      {!rows.length && <p className={styles.emptyText}>{t(needle ? 'experience.design.noMatchingBlocks' : 'experience.design.noBlocks')}</p>}
    </div>
  </div>;
};

export default BlockOutline;
